import { useMemo } from 'react';
import merge from 'deepmerge';
import isEqual from 'lodash/isEqual.js';
import { ApolloClient, createHttpLink, InMemoryCache, } from '@apollo/client';
// eslint-disable-next-line import/extensions
import { setContext } from '@apollo/client/link/context';
import { getConfig } from './config/index.js';
import { hooks } from './wpHooks/index.js';
import { getGraphqlEndpoint } from './lib/getGraphqlEndpoint.js';
import { getAccessToken } from './auth/index.js';
export const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__';
const windowApolloState = typeof window !== 'undefined' ? window[APOLLO_STATE_PROP_NAME] : {};
let apolloClient;
let apolloAuthClient;
function createApolloClient(authenticated = false) {
    const { possibleTypes } = getConfig();
    let inMemoryCacheObject = {
        possibleTypes,
        typePolicies: {
            RootQuery: {
                queryType: true,
            },
            RootMutation: {
                mutationType: true,
            },
        },
    };
    inMemoryCacheObject = hooks.applyFilters('apolloClientInMemoryCacheOptions', inMemoryCacheObject, {});
    const httpLink = createHttpLink({
        uri: getGraphqlEndpoint(),
    });
    const authLink = setContext((_, { headers }) => {
        // get the authentication token from local storage if it exists
        const token = getAccessToken();
        // return the headers to the context so httpLink can read them
        return {
            headers: Object.assign(Object.assign({}, headers), { authorization: token ? `Bearer ${token}` : '' }),
        };
    });
    let apolloClientOptions = {
        ssrMode: typeof window === 'undefined',
        connectToDevTools: typeof window !== 'undefined',
        // uri: getGraphqlEndpoint(),
        link: authenticated ? authLink.concat(httpLink) : httpLink,
        cache: new InMemoryCache(inMemoryCacheObject).restore(windowApolloState),
    };
    apolloClientOptions = hooks.applyFilters('apolloClientOptions', apolloClientOptions, {});
    return new ApolloClient(apolloClientOptions);
}
export function getApolloClient(initialState = null) {
    // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle
    const _apolloClient = apolloClient !== null && apolloClient !== void 0 ? apolloClient : createApolloClient();
    // If your page has Next.js data fetching methods that use Apollo Client, the initial state
    // gets hydrated here
    if (initialState) {
        // Get existing cache, loaded during client side data fetching
        const existingCache = _apolloClient.extract();
        // Merge the initialState from getStaticProps/getServerSideProps in the existing cache
        const data = merge(existingCache, initialState, {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            arrayMerge: (destination, source) => [
                ...source,
                ...destination.filter((d) => source.every((s) => !isEqual(d, s))),
            ],
        });
        // Restore the cache with the merged data
        _apolloClient.cache.restore(data);
    }
    // For SSG and SSR always create a new Apollo Client
    if (typeof window === 'undefined')
        return _apolloClient;
    // Create the Apollo Client once in the client
    if (!apolloClient)
        apolloClient = _apolloClient;
    return _apolloClient;
}
export function getApolloAuthClient() {
    // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle
    const _apolloAuthClient = apolloAuthClient !== null && apolloAuthClient !== void 0 ? apolloAuthClient : createApolloClient(true);
    if (!apolloAuthClient)
        apolloAuthClient = _apolloAuthClient;
    return _apolloAuthClient;
}
export function addApolloState(client, pageProps) {
    if (pageProps === null || pageProps === void 0 ? void 0 : pageProps.props) {
        // eslint-disable-next-line no-param-reassign
        pageProps.props[APOLLO_STATE_PROP_NAME] = client.cache.extract();
    }
    return pageProps;
}
export function useApollo(pageProps) {
    const state = pageProps[APOLLO_STATE_PROP_NAME];
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const store = useMemo(() => getApolloClient(state), [state]);
    return store;
}
